import { useCallback, useMemo } from "react";

import * as Yup from "yup";

import { useLibrary } from "../../../providers";
import { useObjectMemo } from "../../../hooks";
import { useInstitution } from "../../entities";
import { useL10n } from "../../../composites";

import { useNpsRequests } from "./requests";
import { getInitialValidationShape, NpsForm } from "./Nps.form";
import { DEFAULT_NPS_SCORE_CHOICES } from "./constants";

import type { NpsFormValues } from "./Nps.form";

const useDynamicForm = () => {
  const l10n = useL10n();
  const { setValidationSchema, setValues } = NpsForm.useForm();
  return useCallback(
    (survey: API.NPS.Survey, impressionId: string) => {
      const validationShape = survey.questions.reduce(
        (acc, question) => {
          const type =
            question.answer_type === "npsscore" ? Yup.number() : Yup.string();

          const typeAndRequiredOrNot = question.is_required
            ? type.required(l10n.getString("form-generic-validation-required"))
            : type.notRequired();

          acc[question.uuid] = typeAndRequiredOrNot;
          return acc;
        },
        { ...getInitialValidationShape(l10n) },
      );

      const dynamicValidationSchema = Yup.object().shape(validationShape);
      setValidationSchema(dynamicValidationSchema);

      const dynamicInitialValues = survey.questions.reduce(
        (acc, question) => {
          const initialValue = question.answer_type === "npsscore" ? -1 : "";
          acc[question.uuid] = initialValue;
          return acc;
        },
        {
          surveyId: survey.uuid,
          impressionId,
        } as NpsFormValues,
      );

      setValues(dynamicInitialValues);
    },
    [setValidationSchema, setValues, l10n],
  );
};

export const useCheckSurveyAvailable = () => {
  const { fetchSurveys, recordImpression } = useNpsRequests();
  const configureDynamicForm = useDynamicForm();
  const { resetFormAndValidationSchema } = NpsForm.useForm();

  const checkSurveyAvailable = useCallback(async () => {
    const survey = await fetchSurveys();
    if (!survey) {
      return null;
    }

    const impression = await recordImpression({ survey_uuid: survey.uuid });
    if (!impression) {
      return null;
    }

    configureDynamicForm(survey, impression.uuid);
    return survey;
  }, [configureDynamicForm, fetchSurveys, recordImpression]);

  return useObjectMemo({
    checkSurveyAvailable,
    resetFormAndValidationSchema,
  });
};

export const useSurveyTitle = () => {
  const institution = useInstitution();
  const { getString, getParams } = useL10n();
  const fiName = institution?.short_name || "valued";
  return useObjectMemo({
    fl: getParams({
      id: "nps-drawer-title",
      args: {
        fiName,
      },
    }),
    text: getString("nps-drawer-title", {
      fiName,
    }),
  });
};

export const useButtonLabels = () => {
  const { getString } = useL10n();
  return useObjectMemo({
    next: getString("nps-next-button-label", {}, "Next"),
    submit: getString("nps-submit-button-label", {}, "Submit"),
    back: getString("nps-back-button-label", {}, "Back"),
    mostLikely: getString("nps-most-likely-button-label", {}, "Most likely"),
    leastLikely: getString("nps-least-likely-button-label", {}, "Least likely"),
  });
};

export const useNpsScoreQuestion = ({
  uuid: questionId,
  answer_choices,
  is_required,
}: API.NPS.NpsScoreQuestion) => {
  const form = NpsForm.useForm();
  const selection = form.values[questionId];

  const guardedChoices = useMemo(
    () => answer_choices?.choices || DEFAULT_NPS_SCORE_CHOICES,
    [answer_choices?.choices],
  );

  const choices = useMemo(() => {
    return guardedChoices.map((choice) => {
      const selected = selection === choice;
      return {
        choice,
        selected,
        onSelect: () => form.setFieldValue(questionId, choice, true),
      };
    });
  }, [guardedChoices, form, questionId, selection]);

  const buttonDisabled =
    is_required && (selection === -1 || selection === undefined);

  return useObjectMemo({
    buttonDisabled,
    choices,
  });
};

export const useFreeFormQuestions = (questions: API.NPS.FreeFormQuestion[]) => {
  const { getString } = useL10n();

  const getLabel = useCallback(
    (required: boolean) => {
      return getString(
        "nps-answer-freeform-placeholder",
        {
          required: required.toString(),
        },
        `Help improve our service${required ? "" : " (optional)"}`,
      );
    },
    [getString],
  );

  return useMemo(
    () =>
      questions.map((question) => {
        return {
          question,
          label: getLabel(!!question.is_required),
        };
      }),
    [questions, getLabel],
  );
};

export const useSubmitAnswers = (close: () => void) => {
  const { submitAnswer, submitAnswersLoading } = useNpsRequests();
  const form = NpsForm.useForm();
  const { getString } = useL10n();

  const errorMessage = getString(
    "nps-error-toast",
    {},
    "Something went wrong.",
  );
  const successMessage = getString(
    "nps-success-toast",
    {},
    "Feedback submitted.",
  );

  const { throwToast } = useLibrary("toasts");

  const submit = useCallback(async () => {
    const result = await form.submitForm();
    if (!result.success) {
      return false;
    }

    const {
      values: { surveyId, impressionId, ...questionMap },
    } = result;

    const answers = Object.entries(questionMap).map(
      ([question_uuid, value]) => {
        return {
          question_uuid,
          value,
        };
      },
    );

    const results = await Promise.all(
      answers.map(async (a) => {
        if (a.value === "" || a.value === -1) {
          return true;
        }
        return submitAnswer({
          impression_uuid: impressionId,
          question_uuid: a.question_uuid,
          value: a.value,
        });
      }),
    );

    close();

    const allSuccessful = results.every(
      (submitResult) => submitResult === true,
    );

    if (allSuccessful) {
      throwToast({
        kind: "success",
        message: successMessage,
      });
    } else {
      throwToast({
        kind: "error",
        message: errorMessage,
      });
    }

    return allSuccessful;
  }, [close, form, submitAnswer, errorMessage, successMessage, throwToast]);

  return useObjectMemo({
    loading: submitAnswersLoading,
    submit,
    form,
  });
};
